import { fetchQuery, graphql } from 'react-relay';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import getRelayEnvironment from '@jira/platform__graphql-relay-environment/src/index.tsx';
import {
	startCaptureGraphQlErrors,
	stopCaptureGraphQlErrors,
} from '@jira/platform__graphql-relay-errors/src/index.tsx';
import type {
	fetchIssueAttachmentsSmartlinkDataQuery as fetchIssueAttachmentsSmartlinkDataQueryType,
	fetchIssueAttachmentsSmartlinkDataQuery$data as fetchIssueAttachmentsSmartlinkDataQueryResponse,
} from './__generated__/fetchIssueAttachmentsSmartlinkDataQuery.graphql.ts';
import { fetchORSDataSSR, canCallORS, type PrefetchedSmartlinkData } from './fetchORSDataSSR.tsx';

type IssueKey = string;
type CloudId = string;

// Cap SSR ORS calls to 4 total URLs to avoid SSR latency/timeout risk.
const SMART_LINKS_FIRST = 4;
const FETCH_ISSUE_ATTACHMENTS_SMARTLINK_DATA = 'fetch-issue-attachments-smartlink-data';

function extractAttachmentSmartLinkUrls(
	data: fetchIssueAttachmentsSmartlinkDataQueryResponse | null | undefined,
): string[] {
	const edges = data?.jira?.issueByKey?.getUnhydratedConsolidatedResources?.edges ?? [];
	const seen = new Set<string>();
	const urls: string[] = [];
	for (const edge of edges) {
		const url = edge?.node?.contentUrl;
		if (url && !seen.has(url)) {
			seen.add(url);
			urls.push(url);
		}
	}
	// Cap is enforced by the GraphQL query ($first: SMART_LINKS_FIRST) but slice defensively.
	return urls.slice(0, SMART_LINKS_FIRST);
}

/**
 * Fetches ORS smart-link data for eligible 1P consolidated attachment resources (SSR only).
 *
 * Uses a lightweight AGG query that fetches only Confluence and Loom resources (excluding
 * whiteboards and static files) and limits the result to SMART_LINKS_FIRST (4) URLs.
 *
 * Only runs when:
 * - `fg('attachments_consolidation_smart_links_ssr')` is enabled
 * - `expVal('attachments-1p-consolidation-experiment', 'isExperimentEnabled', false)` is true
 * - `canCallORS()` returns true (i.e. not FedRAMP)
 *
 * Returns null on any failure; callers must fall back to the existing client-side SmartCard path.
 */
export const fetchIssueAttachmentsSmartlinkData = async (
	issueKey: IssueKey,
	cloudId: CloudId,
): Promise<PrefetchedSmartlinkData[] | null> => {
	if (
		!fg('attachments_consolidation_smart_links_ssr') ||
		!expVal('attachments-1p-consolidation-experiment', 'isExperimentEnabled', false)
	) {
		return null;
	}

	if (!canCallORS()) {
		return null;
	}

	const captureId = startCaptureGraphQlErrors();
	const environment = getRelayEnvironment();

	let graphqlData: fetchIssueAttachmentsSmartlinkDataQueryResponse | null | undefined;
	try {
		graphqlData = await fetchQuery<fetchIssueAttachmentsSmartlinkDataQueryType>(
			environment,
			// eslint-disable-next-line jira/relay/no-query
			graphql`
				query fetchIssueAttachmentsSmartlinkDataQuery(
					$issueKey: String!
					$cloudId: ID!
					$first: Int!
				) {
					jira {
						issueByKey(key: $issueKey, cloudId: $cloudId) {
							getUnhydratedConsolidatedResources(
								input: {
									first: $first
									filters: [{ integration: CONFLUENCE }, { integration: LOOM }]
								}
							) @optIn(to: "JiraResourceConsolidation") {
								edges {
									node {
										contentUrl
									}
								}
							}
						}
					}
				}
			`,
			{
				issueKey,
				cloudId,
				first: SMART_LINKS_FIRST,
			},
		).toPromise();
	} finally {
		stopCaptureGraphQlErrors(captureId);
	}

	const urls = extractAttachmentSmartLinkUrls(graphqlData);

	if (urls.length === 0) {
		return null;
	}

	return fetchORSDataSSR(urls, FETCH_ISSUE_ATTACHMENTS_SMARTLINK_DATA, undefined, cloudId);
};

export default fetchIssueAttachmentsSmartlinkData;
